import { useState } from 'react';
import { useForm, useWatch, type FieldErrors, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Calendar as CalendarIcon, Sparkles } from 'lucide-react';
import { format, parseISO, eachDayOfInterval } from 'date-fns';
import axiosInstance from '@/lib/axios';

import { toast } from 'sonner';

import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { FormSelectField } from '@/components/forms/form-select-field';
import { FormRadioGroupField } from '@/components/forms/form-radio-group-field';
import { FormSwitchField } from '@/components/forms/form-switch-field';
import { FormDateTimePickerField } from '@/components/forms/form-datetime-picker-field';
import { FormRichTextField } from '@/components/forms/form-rich-text-field';
import { FormBannerUploadField } from '@/components/events/FormBannerUploadField';
import { TicketForm } from '@/components/events/TicketForm';
import { eventCreateFormSchema, eventFormSchema, type EventFormValues } from '@/lib/schemas/event';
import { ticketFromPreset } from '@/lib/event-ticket-defaults';
import { useCommunity } from '@/hooks/use-community';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface EventFormProps {
  initialValues: Partial<EventFormValues>;
  onSubmit: (values: EventFormValues) => Promise<void>;
  submitButtonText: string;
  isSubmitting?: boolean;
  requireAddress?: boolean;
  onCancel?: () => void;
  markAttendanceLocked?: boolean;
}

const EVENT_TYPE_OPTIONS = [
  { value: 'BUSINESS', label: 'Business Event' },
  { value: 'SOCIAL', label: 'Social Event' },
  { value: 'CONCLAVE', label: 'Conclave Event' },
];

const RECURRENCE_OPTIONS = [
  { value: 'ONE_TIME', label: 'One-time Event' },
  { value: 'RECURRING', label: 'Recurring Event' },
];

const LOCATION_TYPE_OPTIONS = [
  { value: 'PHYSICAL', label: 'Physical Location' },
  { value: 'VIRTUAL', label: 'Virtual' },
  { value: 'TO_BE_DECIDED', label: 'To Be Decided' },
];

const PRIVACY_OPTIONS = [
  { value: 'PRIVATE', label: 'Members Only' },
  { value: 'SEMI_PRIVATE', label: 'Members + Visitors (Semi-Private)' },
  { value: 'PUBLIC', label: 'Public (Everyone)' },
];

const DATETIME_DESCRIPTION =
  'Pick a date and time from the calendar, or type them in plain English.';

function isCalendarPopoverTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;
  if (target.closest('[data-calendar-popover]')) return true;
  const wrapper = target.closest('[data-radix-popper-content-wrapper]');
  return Boolean(wrapper?.querySelector('[data-calendar-popover]'));
}

function isCalendarPopoverOpen() {
  return (
    typeof document !== 'undefined' && Boolean(document.querySelector('[data-calendar-popover]'))
  );
}

const RECURRING_DESCRIPTION = 'Type the recurrence pattern in plain English.';

export function EventForm({
  initialValues,
  onSubmit,
  submitButtonText,
  isSubmitting,
  requireAddress,
  onCancel,
  markAttendanceLocked,
}: EventFormProps) {
  const { community } = useCommunity(initialValues.communityId);
  const [isRecurringDialogOpen, setIsRecurringDialogOpen] = useState(false);
  const [recurringDates, setRecurringDates] = useState<
    Array<{ startDate: string; endDate: string } | string>
  >([]);
  const [isParsingRecurrence, setIsParsingRecurrence] = useState(false);
  const [recurrenceError, setRecurrenceError] = useState<string | null>(null);

  const communityId = initialValues.communityId || '';

  const form = useForm<EventFormValues>({
    resolver: zodResolver(
      requireAddress ? eventCreateFormSchema : eventFormSchema
    ) as Resolver<EventFormValues>,
    defaultValues: {
      communityId: '',
      locationType: 'PHYSICAL',
      privacy: 'PUBLIC',
      title: '',
      startDate: '',
      endDate: '',
      address: '',
      virtualUrl: '',
      googleMapLink: '',
      description: '',
      tickets: [
        ticketFromPreset(communityId, 'member'),
        ticketFromPreset(communityId, 'guest'),
      ],
      allowDuplicatePhone: true,
      isRegistrationClosed: false,
      markAttendance: false,
      eventType: 'SOCIAL',
      recurrenceType: 'ONE_TIME',
      recurringFrequency: '',
      recurringStartDate: '',
      recurringEndDate: '',
      recurringTitle: '',
      recurringNotes: '',
      ...initialValues,
    } as EventFormValues,
  });

  async function handleSubmit(values: EventFormValues) {
    const tickets = values.tickets.map(({ preset: _preset, ...ticket }) => ticket);
    await onSubmit({ ...values, tickets });
  }

  const locationType = useWatch({ control: form.control, name: 'locationType' });
  const recurrenceType = useWatch({ control: form.control, name: 'recurrenceType' });

  async function handleCalculateRecurrence() {
    const frequency = form.getValues('recurringFrequency');
    const start = form.getValues('recurringStartDate') || form.getValues('startDate');
    const end = form.getValues('recurringEndDate') || form.getValues('endDate');

    if (!frequency || !frequency.trim()) {
      setRecurrenceError(
        'Please enter a recurring frequency (e.g. Every 1st Friday of the month).'
      );
      return;
    }

    setIsParsingRecurrence(true);
    setRecurrenceError(null);

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await axiosInstance.post<{
        valid: boolean;
        dates: Array<{ startDate: string; endDate: string } | string>;
        count?: number;
        error?: string;
      }>('/api/ai/parse-recurrence', {
        startDate: start || new Date().toISOString(),
        endDate: end || undefined,
        recurringFrequency: frequency,
        timezone,
      });

      if (res.data.valid && Array.isArray(res.data.dates) && res.data.dates.length > 0) {
        setRecurringDates(res.data.dates);
        form.setValue('recurringDates', res.data.dates);
      } else {
        setRecurrenceError(res.data.error || 'Could not find matching dates for this frequency.');
        setRecurringDates([]);
        form.setValue('recurringDates', []);
      }
    } catch (err) {
      console.error(err);
      setRecurrenceError('Failed to calculate recurring dates.');
    } finally {
      setIsParsingRecurrence(false);
    }
  }

  function onInvalid(errors: FieldErrors<EventFormValues>) {
    console.error('Form validation failed:', errors);
    const firstError = Object.values(errors)[0];
    if (
      firstError &&
      typeof firstError === 'object' &&
      'message' in firstError &&
      typeof firstError.message === 'string'
    ) {
      toast.error(`Validation error: ${firstError.message}`);
    } else {
      toast.error('Please check all required fields before submitting.');
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit, onInvalid)} className="space-y-6">
        <FormInputField
          control={form.control}
          name="title"
          label="Event Title"
          placeholder="e.g. Annual Community Meetup"
          required
        />

        {community?.community?.isBusinessEnabled && (
          <FormRadioGroupField
            control={form.control}
            name="eventType"
            label="Please select the type of event"
            options={EVENT_TYPE_OPTIONS}
          />
        )}

        <FormRadioGroupField
          control={form.control}
          name="recurrenceType"
          label="Event Recurrence"
          options={RECURRENCE_OPTIONS}
          onChange={(val, fieldOnChange) => {
            fieldOnChange(val);
            if (val === 'RECURRING') {
              setIsRecurringDialogOpen(true);
            }
          }}
        />

        {recurrenceType === 'RECURRING' && (
          <div className="flex flex-col gap-2 rounded-xl border border-primary/20 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Recurring Schedule</p>
              <p className="text-xs text-muted-foreground">
                {form.watch('recurringFrequency')
                  ? `Frequency: ${form.watch('recurringFrequency')}${
                      recurringDates.length > 0
                        ? ` (${recurringDates.length} dates calculated)`
                        : ''
                    }`
                  : 'Click configure to set recurring frequency and dates.'}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setIsRecurringDialogOpen(true)}
            >
              Configure Schedule
            </Button>
          </div>
        )}

        <FormInputField
          control={form.control}
          name="slug"
          label="Event Slug (Custom URL)"
          placeholder="event-slug"
          prefix="/e/"
          description="Leave empty to auto-generate from title. Only lowercase letters, numbers, and hyphens allowed."
        />

        <FormBannerUploadField control={form.control} name="banner" label="Event Banner" />

        <FormBannerUploadField
          control={form.control}
          name="checkoutBgImage"
          label="Checkout Background Image"
          freeForm
          description="Optional background image for the checkout dialog. A low opacity will be applied automatically."
        />

        {recurrenceType !== 'RECURRING' && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormDateTimePickerField
              control={form.control}
              name="startDate"
              label="Starts"
              placeholder="e.g. next Saturday at 6pm"
              description={DATETIME_DESCRIPTION}
              required
            />
            <FormDateTimePickerField
              control={form.control}
              name="endDate"
              label="Ends"
              placeholder="e.g. tomorrow at 9pm"
              description={DATETIME_DESCRIPTION}
              required
            />
          </div>
        )}

        <FormSelectField
          control={form.control}
          name="locationType"
          label="Location"
          placeholder="Select location type"
          options={LOCATION_TYPE_OPTIONS}
        />

        {locationType === 'PHYSICAL' && (
          <div className="space-y-4">
            <FormInputField
              control={form.control}
              name="address"
              label="Address"
              placeholder="Enter full address"
              required={requireAddress}
            />
            <FormInputField
              control={form.control}
              name="googleMapLink"
              label="Google Maps Link (Optional)"
              placeholder="https://maps.google.com/..."
              description="Add a Google Maps link to help attendees easily navigate to the venue."
            />
          </div>
        )}

        {locationType === 'VIRTUAL' && (
          <FormInputField
            control={form.control}
            name="virtualUrl"
            label="Virtual Event Link"
            placeholder="https://zoom.us/..."
          />
        )}

        <FormRadioGroupField
          control={form.control}
          name="privacy"
          label="Who can attend?"
          options={PRIVACY_OPTIONS}
        />

        <FormRichTextField
          control={form.control}
          name="description"
          label="Description"
          description="Use @ for a member or business, and # for a category."
          mentions
        />

        <FormSwitchField
          control={form.control}
          name="allowDuplicatePhone"
          label="Allow Duplicate Phone Numbers"
          description="If enabled, multiple registrations can use the same phone number. If disabled, each phone number can only register once for this event."
        />

        <FormSwitchField
          control={form.control}
          name="isRegistrationClosed"
          label="Close Registrations"
          description="If enabled, no new registrations will be accepted. Users will see a message indicating that registrations are now closed."
        />

        <FormSwitchField
          control={form.control}
          name="markAttendance"
          label="Mark attendance with scanner"
          description={
            markAttendanceLocked
              ? 'Attendance scanner is already set up for this event.'
              : 'Creates an attendance activity with profile walk-ins, the member ticket as the default walk-in ticket, and duplicate scans turned off.'
          }
          disabled={markAttendanceLocked}
        />

        <TicketForm form={form} communityId={communityId} />

        <div className="flex flex-col-reverse justify-end gap-3 border-t border-border pt-6 md:flex-row">
          {onCancel && (
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:bg-muted hover:text-foreground md:w-auto"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            className="w-full bg-primary px-8 text-white shadow-lg shadow-primary/20 hover:bg-primary/90 md:w-auto"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Please wait
              </>
            ) : (
              submitButtonText
            )}
          </Button>
        </div>

        <Dialog open={isRecurringDialogOpen} onOpenChange={setIsRecurringDialogOpen}>
          <DialogContent
            className="flex max-h-[85vh] flex-col p-0 sm:max-w-xl"
            onPointerDownOutside={(event) => {
              if (isCalendarPopoverTarget(event.target) || isCalendarPopoverOpen()) {
                event.preventDefault();
              }
            }}
            onInteractOutside={(event) => {
              if (isCalendarPopoverTarget(event.target) || isCalendarPopoverOpen()) {
                event.preventDefault();
              }
            }}
            onFocusOutside={(event) => {
              if (isCalendarPopoverTarget(event.target) || isCalendarPopoverOpen()) {
                event.preventDefault();
              }
            }}
          >
            <DialogHeader className="px-6 pt-6 pb-2">
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                Configure Recurring Event
              </DialogTitle>
              <DialogDescription>
                Set the frequency, start date, and end date for your recurring event and preview the
                schedule.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-2">
              <div className="space-y-4">
                <FormInputField
                  control={form.control}
                  name="recurringTitle"
                  label="Title"
                  placeholder="e.g. Weekly Team Sync"
                  required
                />
                <FormInputField
                  control={form.control}
                  name="recurringFrequency"
                  label="Recurring Frequency"
                  placeholder="e.g. Every 1st Friday for 5 days"
                  description={RECURRING_DESCRIPTION}
                  required
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormDateTimePickerField
                    control={form.control}
                    name="recurringStartDate"
                    label="Starts With"
                    placeholder="e.g. next Saturday at 6pm"
                    description={DATETIME_DESCRIPTION}
                    required
                  />
                  <FormDateTimePickerField
                    control={form.control}
                    name="recurringEndDate"
                    label="Ends With"
                    placeholder="e.g. 6 months from now"
                    description={DATETIME_DESCRIPTION}
                    required
                  />
                </div>
                <FormTextareaField
                  control={form.control}
                  name="recurringNotes"
                  label="Notes"
                  placeholder="Additional notes for recurring instances. Use @ for a member or business, # for a category"
                  showCounter={false}
                  mentions
                />

                <div className="pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full h-auto py-2.5 px-4 bg-primary/10 text-primary hover:bg-primary/20 whitespace-normal text-sm font-medium leading-snug flex items-center justify-center gap-2"
                    onClick={handleCalculateRecurrence}
                    disabled={isParsingRecurrence}
                  >
                    {isParsingRecurrence ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                        <span className="text-center">Calculating recurring dates...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
                        <span className="text-center">Calculate & Preview Dates on Calendar</span>
                      </>
                    )}
                  </Button>
                </div>

                {recurrenceError && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                    {recurrenceError}
                  </div>
                )}

                {recurringDates.length > 0 && (
                  <div className="space-y-3 rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground">
                        Generated Dates ({recurringDates.length} occurrences)
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Highlighted on calendar below
                      </span>
                    </div>

                    <div className="flex justify-center overflow-x-auto rounded-lg border bg-background p-2">
                      <Calendar
                        mode="multiple"
                        selected={recurringDates.flatMap((item) => {
                          if (typeof item === 'string') {
                            try {
                              const d = parseISO(item);
                              return !isNaN(d.getTime()) ? [d] : [];
                            } catch {
                              return [];
                            }
                          }
                          try {
                            const start = parseISO(item.startDate);
                            const end = parseISO(item.endDate);
                            if (isNaN(start.getTime())) return [];
                            if (isNaN(end.getTime()) || end < start) return [start];
                            return eachDayOfInterval({ start, end });
                          } catch {
                            return [];
                          }
                        })}
                        onSelect={() => {}}
                        classNames={{
                          month_grid: 'pointer-events-none select-none',
                          day: 'pointer-events-none select-none',
                        }}
                        className="rounded-md"
                      />
                    </div>

                    <div className="max-h-36 overflow-y-auto rounded-lg border bg-muted/30 p-2 text-xs">
                      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                        {recurringDates.map((item, idx) => {
                          const rawStart = typeof item === 'string' ? item : item.startDate;
                          const rawEnd = typeof item === 'string' ? item : item.endDate;
                          let startFmt = rawStart;
                          let endFmt = rawEnd;
                          try {
                            const dtStart = parseISO(rawStart);
                            const dtEnd = parseISO(rawEnd);
                            if (!isNaN(dtStart.getTime())) {
                              startFmt = format(dtStart, 'EEE, MMM d, yyyy h:mm a');
                            }
                            if (!isNaN(dtEnd.getTime())) {
                              if (format(dtStart, 'yyyy-MM-dd') !== format(dtEnd, 'yyyy-MM-dd')) {
                                endFmt = format(dtEnd, 'EEE, MMM d, yyyy h:mm a');
                              } else {
                                endFmt = format(dtEnd, 'h:mm a');
                              }
                            }
                          } catch {
                            // fallback
                          }
                          return (
                            <div
                              key={idx}
                              className="rounded-lg border border-border/60 bg-background p-2 text-center font-medium text-foreground text-xs shadow-sm"
                            >
                              <div className="font-semibold text-primary">{startFmt}</div>
                              {endFmt && endFmt !== startFmt && (
                                <div className="text-[11px] text-muted-foreground mt-0.5 font-medium">
                                  to {endFmt}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="border-t border-border px-6 py-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              {recurringDates.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center sm:text-left">
                  Please calculate recurring dates to complete setup.
                </p>
              ) : (
                <span className="hidden sm:inline" />
              )}
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={() => setIsRecurringDialogOpen(false)}
                disabled={recurringDates.length === 0 || isParsingRecurrence}
              >
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </form>
    </Form>
  );
}
